import { type AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { isProblemDetails, type ProblemDetails } from './http-client-types'

export interface NewErrorOptions {
  forceRefresh?: boolean
}

export const useErrorStore = defineStore('api-error-store', () => {
  const errors = ref<unknown[]>([])

  const errorsGetter = computed(() => errors.value)

  const errorDialogProps = reactive({
    showDialog: false,
    title: '',
    traceId: '',
    message: '',
    options: { forceRefresh: false }
  })

  function closeDialog() {
    errorDialogProps.showDialog = false
    errorDialogProps.traceId = ''
    errorDialogProps.message = ''
    errorDialogProps.title = ''
  }

  function showDialog({
    title,
    message,
    traceId,
    options
  }: {
    title: string
    message: string | undefined
    traceId: string | undefined
    options?: NewErrorOptions
  }) {
    errorDialogProps.showDialog = true
    errorDialogProps.title = title
    errorDialogProps.message = message ?? ''
    errorDialogProps.traceId = traceId ?? ''
    errorDialogProps.options.forceRefresh = options?.forceRefresh === true
  }

  function newError(error: ProblemDetails | AxiosError, options?: NewErrorOptions) {
    // console.log(error)
    errors.value.push(error)
    if (isProblemDetails(error)) {
      if (
        error.status === 400 ||
        error.status === 404 ||
        error.status === 409 ||
        error.status === 500
      ) {
        const _options: NewErrorOptions = {
          forceRefresh: options?.forceRefresh ?? error.status === 500
        }
        showDialog({
          title: error.friendlyTitle ?? error.title,
          message:
            error.detail ??
            `An unexpected error occured. If the error presists please contact your admin or Gatship support for further assistance.`,
          traceId: error.instance,
          options: _options
        })
      }
    } else {
      showDialog({
        title: 'Error',
        message: error.message,
        traceId: error.code
      })
    }
  }

  function clearErrors() {
    errors.value = []
  }

  return {
    errors: errorsGetter,
    errorDialogProps,
    closeDialog,
    newError,
    clearErrors
  }
})
